<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <draggable
                v-model="orderedChoices"
                v-bind="dragOptions"
                :disabled="isDisabled"
                group="people"
                @start="drag = true"
                @end="drag = false"
                @change="orderChange()"
            >
                <div
                    v-for="(choice, index) in orderedChoices"
                    :key="choice.id"
                    class="rounded px-2 py-2 option"
                    :class="{
                        'drag-item': !isDisabled,
                        'bg-light': isDisabled,
                        'bg-correct': isSubmitted && index == choice.correct_order && showCorrectness,
                        'bg-incorrect': isSubmitted && index != choice.correct_order && showCorrectness,
                    }"
                    tabindex="0"
                    :id="'step-order-' + choice.id"
                    @keyup.38="shiftChoice(index, -1, $event)"
                    @keyup.40="shiftChoice(index, 1, $event)"
                >
                    <div class="d-flex align-items-center global-theme-text mx-1">
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-0">
                                    <span
                                        v-if="isDisabled"
                                        class="icon icon-glyphicons-basic-lock text-secondary align-middle mr-custom"
                                    ></span>
                                    <span
                                        v-else
                                        class="icon icon-glyphicons-basic-set align-middle mr-custom"
                                        style="font-size: 90%"
                                    ></span>
                                </div>
                                <div class="flex-grow-1">
                                    <div dir="auto">
                                        <span class="align-middle no-select">{{ choice.text }}</span>
                                    </div>
                                    <div v-show="choice.message && isSubmitted" class="text-secondary small feedback-msg no-select" dir="auto">
                                        <span>{{ choice.message }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow-0">
                            <span
                                v-show="isSubmitted && index == choice.correct_order && showCorrectness"
                                class="
                                    icon icon-lg icon-glyphicons-basic-circle-empty-check
                                    feedback-icon
                                    align-middle
                                    text-success
                                "
                            ></span>
                            <span
                                v-show="isSubmitted && index != choice.correct_order && showCorrectness"
                                class="
                                    icon icon-lg icon-glyphicons-basic-circle-empty-remove
                                    feedback-icon
                                    align-middle
                                    text-danger
                                "
                            ></span>
                        </div>
                    </div>
                </div>
            </draggable>
        </template>
    </base-panel>
</template>

<script>
import BasePanel from "@/components/panels/BasePanel"
import draggable from "vuedraggable"

export default {
    name: "StepOrderPanel",
    props: {
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
            default: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    components: {
        draggable,
        BasePanel,
    },
    data() {
        return {
            orderedChoices: [],
            dragOptions: {
                animation: 200,
                ghostClass: "ghost",
                forceFallback: true
            },
        }
    },
    computed: {
        isComplete() {
            let incorrectChoice = this.orderedChoices.find((choice, index) => index != choice.correct_order)
            return !incorrectChoice
        },
        isSubmitted() {
            return this.savedResponses && this.savedResponses.length > 0
        },
        correctChoicesCount() {
            return this.orderedChoices.filter((choice, index) => index == choice.correct_order).length
        },
        isDisabled() {
            return this.locked || (this.panel.disable_input_after_submit && this.isSubmitted)
        },
        showCorrectness() {
            return this.panel.show_correctness
        },
    },
    watch: {
        orderedChoices(value) {
            this.$emit("update:orderedChoices", value)
        },
        isComplete(value) {
            this.$emit("update:isComplete", value)
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
        },
        correctChoicesCount(value) {
            this.$emit("update:correctChoicesCount", value)
        },
        savedResponses() {
            this.initPanel()
        },
    },
    methods: {
        initPanel() {
            if (this.savedResponses && this.savedResponses.length) {
                // Create an editable copy of saved responses to modify.
                let choiceResponses = this.savedResponses.slice(0)
                // This is a hack - check for the existence of an 'edited' choice response.
                // If one is found, assume that this means that there's a set of edited choice
                // responses (i.e. steps) in the saved response list.
                let hasEditedResponses = Boolean(
                    this.savedResponses.find(r => r.is_edited_response)
                )
                if (hasEditedResponses) {
                    choiceResponses = choiceResponses.filter(r => r.is_edited_response)
                }
                // Order the saved responses in the order the learner submitted them in.
                choiceResponses.sort((a, b) => {
                    if (a.choice_order > b.choice_order) {
                        return 1
                    }
                    return -1
                })
                this.orderedChoices = choiceResponses.map((response) => {
                    return response.choice
                })
                this.$emit("update:isSubmitted", true)
            } else {
                // Default display of choice options.
                this.orderedChoices = this.panel.choices
            }
        },
        orderChange() {
            this.$emit("update:isEdited", true)
        },
        shiftChoice(index, increment) {
            if (this.isDisabled) {
                return
            }
            // Don't do anything if series can't be moved further up or down.
            if ((index == 0 && increment == -1) || (index + 1 >= this.orderedChoices.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let choice = this.orderedChoices.splice(index, 1)[0]
            // Put it back at the new position.
            this.orderedChoices.splice(index + increment, 0, choice)
            // Re-focus the step.
            this.$nextTick(() => document.getElementById("step-order-" + choice.id).focus())
            // Emit that the order has changed.
            this.orderChange()
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleSubmit() {
            this.$emit("submit")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    mounted() {
        this.initPanel()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.drag-item {
    cursor: move; /* fallback if grab cursor is unsupported */
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
}

.drag-item:active {
    cursor: grabbing; /* fallback if grab cursor is unsupported */
    cursor: -moz-grab;
    cursor: -webkit-grab;
}

.feedback-msg {
    padding-top: 3px;
    line-height: 1.4;
}

.option {
    margin-bottom: 0.6rem;
    box-shadow: 0 0.02rem 0.12rem rgba($black, 0.075);
    background-color: #e6e9ef;
}

.bg-correct {
    background-color: #d3ecd9 !important;
}

.bg-incorrect {
    background-color: #f8d7da !important;
}

.mr-custom {
    margin-right: 0.7rem;
}
</style>